import { useState, useEffect, useRef } from 'react';
import { Icon } from '@atoms/index';
import { AlertModal } from '@organisms/index';
import { useLocationStore } from '@store/locationStore';
import { useAuthStore } from '@store/authStore';
import { useCartStore } from '@store/cartStore';
import { addressService } from '@services/addressService';
import { geoService } from '@services/geoService';
import { geocodingService } from '@services/geocodingService';
import type { UserAddress, GeocodingResult } from '@/types';
import type { AlertModalVariant } from './AlertModal';

interface AddressSelectorProps {
  onAddressChange?: () => void;
  restaurantId?: string | null;
}

const AddressSelector = ({ onAddressChange, restaurantId }: AddressSelectorProps) => {
  const { isAuthenticated, user } = useAuthStore();
  const { activeAddress, serviceZone, setActiveAddress, setServiceZone } = useLocationStore();
  const { setServiceZone: setCartServiceZone } = useCartStore();
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [savedAddresses, setSavedAddresses] = useState<UserAddress[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingZone, setIsLoadingZone] = useState(false);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; variant: AlertModalVariant }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
  });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load saved addresses if authenticated and user role is USER (not RESTAURANT/ADMIN)
  useEffect(() => {
    if (isAuthenticated && user?.role === 'USER') {
      loadSavedAddresses();
    }
  }, [isAuthenticated, user?.role]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Search addresses with debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchAddresses(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const loadSavedAddresses = async () => {
    if (user?.role && user.role !== 'USER') {
      return;
    }

    try {
      const addresses = await addressService.getAddresses();
      setSavedAddresses(addresses);

      const defaultAddress = addresses.find((addr) => addr.isDefault) || addresses[0];
      const shouldApplyDefault =
        defaultAddress &&
        defaultAddress.location &&
        (!activeAddress || activeAddress.addressId !== defaultAddress.id);

      if (shouldApplyDefault) {
          const location = defaultAddress.location as { type: string; coordinates: number[] };
          const [longitude, latitude] = location.coordinates;

          setActiveAddress({
            displayName: defaultAddress.title,
            fullAddress: defaultAddress.fullAddress,
            city: defaultAddress.city,
            district: defaultAddress.district,
            latitude,
            longitude,
            addressId: defaultAddress.id,
          });

          let zone = await geoService.checkServiceZone(latitude, longitude);
          if (!zone && defaultAddress.city?.trim()) {
            const cityZones = await geoService.getZonesByCity(defaultAddress.city);
            const districtNorm = (defaultAddress.district ?? '').trim().toLowerCase();
            zone =
              cityZones.find((z) => z.district?.trim().toLowerCase() === districtNorm) ??
              cityZones[0] ??
              null;
          }
          if (zone) {
            setServiceZone(zone);
            await setCartServiceZone(zone.id).catch(() => {});
          }
      }
    } catch (err: any) {
      // Silently fail for 403 errors (RESTAURANT/ADMIN roles don't have access)
      if (err?.response?.status === 403) {
        console.debug('Address access denied (expected for non-USER roles)');
        return;
      }
      console.error('Failed to load saved addresses:', err);
    }
  };

  const searchAddresses = async (query: string) => {
    setIsSearching(true);
    try {
      const results = await geocodingService.searchAddresses(query);
      setSearchResults(results);
    } catch (err: any) {
      console.error('Address search error:', err);
      setSearchResults([]);
      
      if (err?.response?.status === 500) {
        const errorMessage = err?.response?.data?.error?.message || '';
        if (errorMessage.includes('API key not configured') || errorMessage.includes('access token not configured')) {
          console.warn('Geocoding service not configured. Please add GOOGLE_PLACES_API_KEY and MAPBOX_ACCESS_TOKEN to .env file.');
        }
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddressSelect = async (address: GeocodingResult | UserAddress) => {
    let latitude: number;
    let longitude: number;
    let displayName: string;
    let fullAddress: string;
    let city: string;
    let district: string;
    let addressId: string | undefined;

    if ('location' in address) {
      // UserAddress
      const location = address.location as { type: string; coordinates: number[] };
      [longitude, latitude] = location.coordinates;
      displayName = address.title;
      fullAddress = address.fullAddress;
      city = address.city;
      district = address.district;
      addressId = address.id;
    } else {
      // GeocodingResult
      latitude = address.latitude;
      longitude = address.longitude;
      displayName = address.displayName;
      fullAddress = address.fullAddress;
      city = address.city;
      district = address.district;
    }

    // Set active address
    setActiveAddress({
      displayName,
      fullAddress,
      city,
      district,
      latitude,
      longitude,
      addressId,
    });

    setIsLoadingZone(true);
    try {
      if (restaurantId) {
        const zone = await geoService.checkRestaurantDeliveryZone(restaurantId, latitude, longitude);
        if (zone) {
          setIsOpen(false);
          setSearchQuery('');
          setSearchResults([]);
          onAddressChange?.();
        } else {
          setAlertState({
            isOpen: true,
            title: 'Hizmet bölgesi dışında',
            message: 'Bu adres hizmet bölgesi dışında. Lütfen hizmet bölgesi içindeki bir adres seçin.',
            variant: 'warning',
          });
        }
        return;
      }

      let zone = await geoService.checkServiceZone(latitude, longitude);

      if (!zone && city?.trim()) {
        const cityZones = await geoService.getZonesByCity(city);
        const districtNorm = (district ?? '').trim().toLowerCase();
        zone =
          cityZones.find((z) => z.district?.trim().toLowerCase() === districtNorm) ??
          cityZones[0] ?? null;
      }

      if (zone) {
        setServiceZone(zone);
        await setCartServiceZone(zone.id).catch((err) => {
          console.error('Failed to set service zone in cart:', err);
        });

        setIsOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        onAddressChange?.();
      } else {
        setAlertState({
          isOpen: true,
          title: 'Hizmet bölgesi dışında',
          message: 'Bu adres hizmet bölgesi dışında. Lütfen hizmet bölgesi içindeki bir adres seçin.',
          variant: 'warning',
        });
      }
    } catch (err) {
      console.error('Failed to check service zone:', err);
      setAlertState({
        isOpen: true,
        title: 'Hata',
        message: 'Konum kontrolü yapılırken bir hata oluştu.',
        variant: 'danger',
      });
    } finally {
      setIsLoadingZone(false);
    }
  };

  const displayText = activeAddress
    ? (activeAddress.displayName?.trim() || `${activeAddress.district}, ${activeAddress.city}`)
    : 'Teslimat adresi seçin';
  const displayTextTruncated = displayText.length > 32 ? `${displayText.slice(0, 29)}…` : displayText;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Address Display Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-primary transition-colors text-sm font-medium text-gray-700 max-w-xs"
      >
        <Icon name="location" size={18} className="text-primary flex-shrink-0" />
        <span className="truncate">{displayTextTruncated}</span>
        <Icon name={isOpen ? 'close' : 'plus'} size={16} className="text-gray-400 flex-shrink-0" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] flex flex-col">
          {/* Search Input */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Adres ara (örn: Seyhan, Adana)"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              <Icon
                name="search"
                size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {/* Saved Addresses (if authenticated and no search query) */}
            {isAuthenticated && !searchQuery && savedAddresses.length > 0 && (
              <div className="p-2">
                <p className="text-xs font-semibold text-gray-500 uppercase px-2 mb-2">
                  Kayıtlı Adreslerim
                </p>
                {savedAddresses.map((address) => (
                  <button
                    key={address.id}
                    onClick={() => handleAddressSelect(address)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <p className="font-medium text-gray-900 text-sm">{address.title}</p>
                    <p className="text-xs text-gray-600 truncate">{address.fullAddress}</p>
                    {address.isDefault && (
                      <span className="text-xs text-primary">(Varsayılan)</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Search Results */}
            {searchQuery && (
              <div className="p-2">
                {searchResults.length > 0 ? (
                  <>
                    <p className="text-xs font-semibold text-gray-500 uppercase px-2 mb-2">
                      Arama Sonuçları
                    </p>
                    {searchResults.map((result, index) => (
                      <button
                        key={index}
                        onClick={() => handleAddressSelect(result)}
                        disabled={isLoadingZone}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        <p className="font-medium text-gray-900 text-sm">{result.displayName}</p>
                        {result.district && result.city && (
                          <p className="text-xs text-gray-600">
                            {result.district}, {result.city}
                          </p>
                        )}
                      </button>
                    ))}
                  </>
                ) : (
                  !isSearching && (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      Sonuç bulunamadı
                    </div>
                  )
                )}
              </div>
            )}

            {/* Empty State */}
            {!searchQuery && (!isAuthenticated || savedAddresses.length === 0) && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-500 mb-2">
                  Adres aramak için yukarıdaki alana yazın
                </p>
                {!isAuthenticated && (
                  <p className="text-xs text-gray-400">
                    Kayıtlı adreslerinizi görmek için giriş yapın
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Loading Zone Indicator */}
          {isLoadingZone && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span>Hizmet bölgesi kontrol ediliyor...</span>
              </div>
            </div>
          )}
        </div>
      )}
      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
};

export default AddressSelector;
